<template>
  <section class="banner-section" v-if="banners.length > 0">
    <div class="banner-container">
      <div class="banner-wrapper">
        <div class="banner-track" :style="trackStyle">
          <div 
            v-for="(banner, index) in banners" 
            :key="banner.id || index" 
            class="banner-slide"
          >
            <router-link :to="banner.link || '#'" class="banner-link">
              <img :src="banner.image" :alt="banner.title" class="banner-image" />
              
              <!-- 상단 뱃지들 -->
              <div class="banner-badges">
                <span v-if="banner.isPremium" class="badge premium">PREMIUM</span>
                <span v-if="banner.isPick" class="badge pick">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  PICK
                </span>
                <span v-if="banner.isUrgent" class="badge urgent">급구</span>
                <span v-if="banner.isNew" class="badge new">NEW</span>
                <span v-if="banner.isFeatured" class="badge featured">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .494.083.964.237 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z"/>
                  </svg>
                </span>
              </div>

              <!-- 하단 정보 -->
              <div class="banner-content">
                <div class="banner-meta">
                  <span v-if="banner.region" class="meta-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                      <circle cx="12" cy="10" r="3"/>
                    </svg>
                    {{ banner.region }}
                  </span>
                  <span v-if="banner.industry" class="meta-item">{{ banner.industry }}</span>
                </div>
                
                <h2 class="banner-title">{{ banner.title }}</h2>
                
                <div class="banner-info">
                  <span v-if="banner.salary" class="salary">{{ banner.salary }}</span>
                  <span v-if="banner.jobType" class="job-type">{{ banner.jobType }}</span>
                </div>

                <p v-if="banner.description" class="banner-desc">{{ banner.description }}</p>
              </div>
            </router-link>
          </div>
        </div>
      </div>

      <!-- 네비게이션 버튼 -->
      <button class="nav-btn prev" @click="prev">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M15 18l-6-6 6-6"/>
        </svg>
      </button>
      <button class="nav-btn next" @click="next">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M9 18l6-6-6-6"/>
        </svg>
      </button>
    </div>

    <!-- 인디케이터 -->
    <div class="banner-indicators">
      <button 
        v-for="index in maxIndex + 1" 
        :key="index - 1"
        class="indicator"
        :class="{ active: currentIndex === index - 1 }"
        @click="goToSlide(index - 1)"
      />
    </div>
  </section>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore'
import { db } from '@/services/firebase'

export default {
  name: 'BannerSection',
  setup() {
    const currentIndex = ref(0)
    const isMobile = ref(false)
    const banners = ref([])
    let interval = null

    const fetchBanners = async () => {
      try {
        const q = query(
          collection(db, 'banners'),
          where('isActive', '==', true),
          orderBy('order', 'asc')
        )
        const snapshot = await getDocs(q)
        banners.value = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
      } catch (e) {
        console.error('배너 로드 실패:', e)
      }
    }

    const slidesPerView = computed(() => isMobile.value ? 1 : 2)
    
    const maxIndex = computed(() => {
      return Math.max(0, banners.value.length - slidesPerView.value)
    })

    const trackStyle = computed(() => {
      const slideWidth = isMobile.value ? 100 : 50
      const gapPercent = isMobile.value ? 0 : 0.7
      return {
        transform: `translateX(calc(-${currentIndex.value * slideWidth}% - ${currentIndex.value * gapPercent}%))`
      }
    })

    const next = () => {
      if (currentIndex.value < maxIndex.value) {
        currentIndex.value++
      } else {
        currentIndex.value = 0
      }
    }

    const prev = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--
      } else {
        currentIndex.value = maxIndex.value
      }
    }

    const goToSlide = (index) => {
      currentIndex.value = index
    }

    const checkMobile = () => {
      isMobile.value = window.innerWidth <= 768
    }

    const startAutoPlay = () => {
      interval = setInterval(next, 5000)
    }

    const stopAutoPlay = () => {
      if (interval) clearInterval(interval)
    }

    onMounted(async () => {
      checkMobile()
      window.addEventListener('resize', checkMobile)
      await fetchBanners()
      if (banners.value.length > 0) {
        startAutoPlay()
      }
    })

    onUnmounted(() => {
      window.removeEventListener('resize', checkMobile)
      stopAutoPlay()
    })

    return {
      banners,
      currentIndex,
      maxIndex,
      trackStyle,
      next,
      prev,
      goToSlide
    }
  }
}
</script>

<style scoped>
.banner-section {
  padding: 24px 24px 0;
}

.banner-container {
  position: relative;
}

.banner-wrapper {
  overflow: hidden;
  border-radius: 16px;
}

.banner-track {
  display: flex;
  gap: 16px;
  transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.banner-slide {
  flex: 0 0 calc(50% - 8px);
  min-width: calc(50% - 8px);
}

.banner-link {
  display: block;
  position: relative;
  padding-bottom: 56.25%;
  border-radius: 16px;
  overflow: hidden;
  background: #f2f4f6;
}

.banner-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 상단 뱃지 */
.banner-badges {
  position: absolute;
  top: 16px;
  left: 16px;
  display: flex;
  gap: 6px;
  z-index: 2;
  align-items: center;
}

.badge {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.badge.premium {
  background: linear-gradient(135deg, #3182f6 0%, #1b64da 100%);
  color: #fff;
  letter-spacing: 0.5px;
}

.badge.pick {
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(168, 85, 247, 0.3);
}

.badge.urgent {
  background: #ef4444;
  color: #fff;
}

.badge.new {
  background: #22c55e;
  color: #fff;
  letter-spacing: 0.5px;
}

.badge.featured {
  width: 22px;
  height: 22px;
  padding: 0;
  background: transparent;
  color: #3182f6;
  border-radius: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.badge.featured svg {
  width: 22px;
  height: 22px;
  filter: drop-shadow(0 1px 2px rgba(49, 130, 246, 0.3));
}

/* 하단 콘텐츠 */
.banner-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 24px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.75) 0%, rgba(0, 0, 0, 0.4) 60%, transparent 100%);
  color: #fff;
}

.banner-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  opacity: 0.9;
}

.meta-item svg {
  opacity: 0.8;
}

.banner-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  letter-spacing: -0.3px;
  line-height: 1.3;
}

.banner-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.salary {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}

.job-type {
  font-size: 13px;
  opacity: 0.85;
}

.banner-desc {
  font-size: 13px;
  opacity: 0.8;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 네비게이션 버튼 */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #191f28;
  opacity: 0;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  z-index: 10;
}

.banner-container:hover .nav-btn {
  opacity: 1;
}

.nav-btn:hover {
  border-color: #3182f6;
  color: #3182f6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.nav-btn.prev {
  left: -22px;
}

.nav-btn.next {
  right: -22px;
}

/* 인디케이터 */
.banner-indicators {
  display: flex;
  justify-content: center;
  gap: 8px;
  padding-top: 16px;
}

.indicator {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d1d5db;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
}

.indicator.active {
  width: 24px;
  border-radius: 4px;
  background: #3182f6;
}

/* 반응형 */
@media (max-width: 768px) {
  .banner-section {
    padding: 16px 16px 0;
  }

  .banner-wrapper {
    border-radius: 12px;
  }

  .banner-track {
    gap: 0;
  }

  .banner-slide {
    flex: 0 0 100%;
    min-width: 100%;
  }

  .banner-link {
    border-radius: 12px;
  }

  .banner-badges {
    top: 12px;
    left: 12px;
  }

  .badge {
    padding: 3px 8px;
    font-size: 10px;
    gap: 3px;
  }

  .badge.featured {
    width: 18px;
    height: 18px;
  }

  .badge.featured svg {
    width: 18px;
    height: 18px;
  }

  .badge.pick svg {
    width: 8px;
    height: 8px;
  }

  .banner-content {
    padding: 16px;
  }

  .banner-meta {
    gap: 8px;
    margin-bottom: 6px;
  }

  .meta-item {
    font-size: 11px;
  }

  .banner-title {
    font-size: 16px;
    margin-bottom: 6px;
  }

  .banner-info {
    gap: 6px;
    margin-bottom: 4px;
  }

  .salary {
    padding: 3px 8px;
    font-size: 12px;
  }

  .job-type {
    font-size: 12px;
  }

  .banner-desc {
    font-size: 12px;
    -webkit-line-clamp: 1;
  }

  .nav-btn {
    display: none;
  }

  .banner-indicators {
    padding-top: 12px;
  }
}
</style>